/**
 * Les étiquettes de la semaine — un rayon, une fournée.
 *
 * Chaque semaine, les étiquettes de prix des Fruits & Légumes sont refaites :
 * les cageots bougent, les étiquettes se salissent, la marchandise change de
 * place. Ce n'est pas une affaire de prix qui changent — c'est une tournée.
 * Elle se faisait UNE PAR UNE : 233 étiquettes, une demi-heure (signalé le
 * 10/09/2026).
 *
 * Ici, tout le rayon est coché d'avance et on DÉCOCHE ce qui est déjà fait.
 * C'est le sens du geste : la question n'est pas « laquelle imprimer ? » mais
 * « laquelle ai-je déjà faite ? ».
 *
 * Pour que « déjà imprimé » veuille dire quelque chose, chaque impression est
 * journalisée — y compris celles qui sortent par la Zebra du Mac mini, ce que
 * l'ancien chemin ne faisait pas (journal vide au 10/09/2026, alors que les
 * étiquettes sortent chaque semaine).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { labelTemplatesApi } from '../api/label-templates.api';
import { LabelTemplate, LabelTemplateType, PriceChangesResponse } from '../types/label-templates.types';
import { getHardwareBridgeStatus, hardwareBridgeZebra } from '../services/printers/HardwareBridgeAdapter';
import { api } from '../services/api';
import { logger } from '../utils/logger';

type Ligne = PriceChangesResponse['products'][number];

/** Les rayons de la tournée hebdomadaire, préselectionnés. */
const RAYONS_PAR_DEFAUT = ['Fruits', 'Légumes'];

const jour = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('fr-BE', { day: 'numeric', month: 'short' }) : null;

const joursDepuis = (iso: string | null) =>
  iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : null;

export default function EtiquettesSemainePage() {
  const [rayonsDispo, setRayonsDispo] = useState<string[]>([]);
  const [rayons, setRayons] = useState<string[]>(RAYONS_PAR_DEFAUT);
  const [lignes, setLignes] = useState<Ligne[]>([]);
  const [coches, setCoches] = useState<Set<string>>(new Set());
  const [templates, setTemplates] = useState<LabelTemplate[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [recherche, setRecherche] = useState('');
  const [loading, setLoading] = useState(true);
  const [impression, setImpression] = useState<string | null>(null);
  /**
   * Par défaut, seuls les produits qu'on a réellement. Une étiquette pour un
   * article absent du rayon, c'est du papier perdu — et une étagère qui annonce
   * un prix pour du vide.
   */
  const [enStock, setEnStock] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const cats = await api.get<Array<{ id: string; name: string; parent_id?: string | null }>>(
          '/categories',
        );
        setRayonsDispo((cats || []).filter((c) => !c.parent_id).map((c) => c.name).sort());
      } catch {
        setRayonsDispo(RAYONS_PAR_DEFAUT);
      }
      try {
        const tpl = await labelTemplatesApi.findAll();
        setTemplates(tpl);
        const parDefaut =
          tpl.find((t) => t.type === LabelTemplateType.SHELF && t.is_default) ||
          tpl.find((t) => t.type === LabelTemplateType.SHELF);
        if (parDefaut) setTemplateId(parDefaut.id);
      } catch {
        /* la liste des modèles n'est pas vitale au chargement */
      }
    })();
  }, []);

  const charger = useCallback(async () => {
    if (rayons.length === 0) {
      setLignes([]);
      setCoches(new Set());
      return;
    }
    try {
      setLoading(true);
      const res = await labelTemplatesApi.getLabelsToPrint(rayons, true, enStock);
      const produits = res?.products || [];
      setLignes(produits);
      // Tout coché d'avance : on décoche ce qui est fait, on ne coche pas 233 fois.
      setCoches(new Set(produits.map((p) => p.id)));
    } catch (err) {
      logger.error('Chargement des étiquettes de la semaine:', err);
    } finally {
      setLoading(false);
    }
  }, [rayons, enStock]);

  useEffect(() => {
    void charger();
  }, [charger]);

  const visibles = useMemo(() => {
    if (!recherche.trim()) return lignes;
    const q = recherche.toLowerCase();
    return lignes.filter(
      (l) =>
        l.name.toLowerCase().includes(q) ||
        (l.code || '').toLowerCase().includes(q) ||
        (l.barcode || '').includes(q),
    );
  }, [lignes, recherche]);

  const basculer = (id: string) =>
    setCoches((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });

  /** Décoche ce qui a été imprimé récemment : le geste central de l'écran. */
  const decocherRecentes = (jours: number) =>
    setCoches((prev) => {
      const s = new Set(prev);
      for (const l of lignes) {
        const d = joursDepuis(l.last_printed_at);
        if (d !== null && d < jours) s.delete(l.id);
      }
      return s;
    });

  const imprimer = async () => {
    const aImprimer = lignes.filter((l) => coches.has(l.id));
    if (aImprimer.length === 0 || !templateId) return;
    if (
      !window.confirm(
        `Imprimer ${aImprimer.length} étiquette${aImprimer.length > 1 ? 's' : ''} ?\n\n` +
          'Vérifie que le rouleau est chargé : elles sortent à la suite.',
      )
    )
      return;

    try {
      setImpression('Préparation…');
      const labels = aImprimer.map((l) => ({
        productId: l.id,
        templateId: l.default_template_id || templateId,
      }));

      // UN appel pour tout le ZPL : une requête par étiquette, c'était 233
      // allers-retours pour un geste qui doit en prendre trois.
      const lot = await labelTemplatesApi.previewBatch(labels);
      const echecs = lot.items.filter((i) => !i.zpl);

      const bridge = await getHardwareBridgeStatus();
      const zebraDispo = bridge?.hardware?.zebra?.available ?? false;

      if (!zebraDispo) {
        alert(
          (bridge?.hardware?.zebra?.connected === false
            ? "La Zebra n'est pas branchée sur ce poste.\n\n"
            : "L'imprimante Zebra n'est pas accessible depuis ce poste.\n\n") +
            'Ouvre cette page sur la caisse à laquelle la Zebra est branchée.',
        );
        return;
      }

      setImpression(`Impression de ${lot.generated} étiquette(s)…`);
      // Le ZPL est concaténé : l'imprimante enchaîne les blocs elle-même.
      const res = await hardwareBridgeZebra.printZPL(lot.zpl);
      if (!res.success) throw new Error(res.message || "L'imprimante a refusé le travail");

      // Journaliser APRÈS coup : c'est ce qui fera dire « déjà imprimé » la
      // semaine prochaine. Un échec ici ne remet pas les étiquettes dans le
      // rouleau, il ne doit donc pas être présenté comme un échec d'impression.
      try {
        await labelTemplatesApi.logPrinted(
          lot.items.filter((i) => i.zpl).map((i) => ({
            productId: i.product_id,
            templateId: labels.find((l) => l.productId === i.product_id)!.templateId,
          })),
        );
      } catch (err) {
        logger.error('Journal des étiquettes imprimées:', err);
      }

      alert(
        `${lot.generated} étiquette(s) imprimée(s).` +
          (echecs.length ? `\n\n${echecs.length} n'ont pas pu être générées.` : ''),
      );
      await charger();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Échec de l'impression");
    } finally {
      setImpression(null);
    }
  };

  const nbCoches = lignes.filter((l) => coches.has(l.id)).length;
  const dejaFaites = lignes.filter((l) => {
    const d = joursDepuis(l.last_printed_at);
    return d !== null && d < 7;
  }).length;

  return (
    <div className="p-4 md:p-6 max-w-4xl">
      <header className="mb-5">
        <h1 className="text-2xl font-bold text-gray-900">Étiquettes de la semaine</h1>
        <p className="text-sm text-gray-600 mt-1">
          Tout le rayon est coché : décoche ce que tu as déjà fait, puis imprime en une fois.
        </p>
      </header>

      {/* Rayons */}
      <div className="flex flex-wrap gap-2 mb-4">
        {rayonsDispo.map((r) => {
          const actif = rayons.includes(r);
          return (
            <button
              key={r}
              onClick={() => setRayons((prev) => (actif ? prev.filter((x) => x !== r) : [...prev, r]))}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                actif
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
              }`}
            >
              {r}
            </button>
          );
        })}
      </div>

      <label className="flex items-center gap-2 mb-4 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={enStock}
          onChange={(e) => setEnStock(e.target.checked)}
          className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
        />
        Seulement les produits en stock (rayon ou réserve)
      </label>

      {/* Barre d'action */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input
          type="text"
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          placeholder="Filtrer par nom, code, code-barres…"
          className="flex-1 min-w-[14rem] px-3 py-2 border border-gray-300 rounded-lg"
        />
        <button
          onClick={() => setCoches(new Set(lignes.map((l) => l.id)))}
          className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium hover:bg-gray-50"
        >
          Tout cocher
        </button>
        <button
          onClick={() => setCoches(new Set())}
          className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium hover:bg-gray-50"
        >
          Tout décocher
        </button>
        {dejaFaites > 0 && (
          <button
            onClick={() => decocherRecentes(7)}
            className="px-3 py-2 rounded-lg border border-amber-400 text-amber-800 text-sm font-medium hover:bg-amber-50"
          >
            Décocher les {dejaFaites} imprimée{dejaFaites > 1 ? 's' : ''} cette semaine
          </button>
        )}
      </div>

      {/* Impression */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border border-emerald-300 rounded-lg p-3 mb-4 flex flex-wrap items-center gap-3">
        <span className="font-semibold text-emerald-900 tabular-nums">
          {nbCoches} étiquette{nbCoches > 1 ? 's' : ''} à imprimer
        </span>
        {templates.length > 1 && (
          <select
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            className="px-2 py-1.5 border border-gray-300 rounded-md text-sm"
          >
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        )}
        <button
          onClick={() => void imprimer()}
          disabled={nbCoches === 0 || !!impression || !templateId}
          className="ml-auto px-4 py-2.5 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-40 min-h-[44px]"
        >
          {impression || `🏷️ Imprimer les ${nbCoches}`}
        </button>
      </div>

      {loading ? (
        <p className="text-gray-500">Chargement…</p>
      ) : lignes.length === 0 ? (
        <p className="text-gray-500">
          Aucun produit avec un prix dans {rayons.length ? 'ces rayons' : 'aucun rayon sélectionné'}.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 bg-white rounded-lg border border-gray-200">
          {visibles.map((l) => {
            const d = joursDepuis(l.last_printed_at);
            const recent = d !== null && d < 7;
            return (
              <li key={l.id}>
                <label className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={coches.has(l.id)}
                    onChange={() => basculer(l.id)}
                    className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium text-gray-900 truncate">{l.name}</span>
                    <span className="block text-xs text-gray-500">
                      {l.category_name || 'Sans rayon'}
                      {l.last_printed_at
                        ? ` · imprimée le ${jour(l.last_printed_at)}`
                        : ' · jamais imprimée'}
                      {l.stock_total != null && (
                        <>
                          {' · '}
                          {l.stock_display ? `${l.stock_display} en rayon` : 'rien en rayon'}
                          {l.stock_total > (l.stock_display || 0)
                            ? `, ${Math.round((l.stock_total - (l.stock_display || 0)) * 1000) / 1000} en réserve`
                            : ''}
                        </>
                      )}
                      {l.status === 'price_changed' && (
                        <span className="text-amber-700 font-medium"> · prix changé depuis</span>
                      )}
                    </span>
                  </span>
                  <span className="font-semibold tabular-nums text-gray-900">
                    {l.current_price_cents != null
                      ? `${(l.current_price_cents / 100).toFixed(2)} €`
                      : '—'}
                  </span>
                  {recent && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 whitespace-nowrap">
                      faite
                    </span>
                  )}
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
